import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { Timelog, useGetTimelogs } from '@/api/queries/timelog/useGetTimelogs';
import { GetTimelogsInput } from '@/gql/graphql';
import { usePaginationState } from '@/ui/pages/projectPage/components/MaterialsPanel/materialsPanelHooks';
import { endOfWeek, startOfWeek } from 'date-fns';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AssigneeFilter } from './AssigneeFilter';
import { DateFilter } from './DateFilter';
import { ListedTimelogs } from './ListedTimelogs';
import { TeamFilter } from './TeamFilter';
import { TimelogsByUserTable } from './TimelogsByUserTable';
import { TimelogsTotalsSummary } from './TimelogsTotalsSummary';
import { TypeFilter } from './TypeFilter';

export type DialogState =
  | { type: 'createTimelog' }
  | { type: 'editTimelog'; timelogId: string }
  | { type: 'deleteTimelog'; timelogId: string }
  | null;

// 'list' = the flat per-timelog table; 'byUser' = the per-user summary.
export type TimelogsViewMode = 'list' | 'byUser';

export type TimelogReportDatePeriod =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

type DialogAction =
  | { type: 'OPEN_DIALOG'; payload: DialogState }
  | { type: 'CLOSE_DIALOG' };

const dialogReducer = (
  state: DialogState,
  action: DialogAction
): DialogState => {
  switch (action.type) {
    case 'OPEN_DIALOG':
      return action.payload;
    case 'CLOSE_DIALOG':
      return null;
    default:
      return state;
  }
};

const useInputPaginationState = (
  pageSize: number,
  defaultInput: Partial<GetTimelogsInput>
) => {
  const { pagination, ...paginationActions } = usePaginationState(pageSize);
  const [input, setInput] = useState<GetTimelogsInput>(defaultInput);

  return {
    reset: () => {
      setInput(defaultInput);
      paginationActions.setCurrentPage(1);
    },
    pagination,
    ...paginationActions,
    input: {
      ...input,
    },
    setSort: (sort: {
      sortDirection: GetTimelogsInput['sortDirection'];
      sortBy: GetTimelogsInput['sortBy'];
    }) => {
      setInput((old) => ({ ...old, ...sort }));
    },
    setFilter: (
      newInput: Partial<
        Pick<
          GetTimelogsInput,
          'assignees' | 'timelogTypeIds' | 'periodStart' | 'periodEnd'
        >
      >
    ) => {
      setInput((old) => ({ ...old, ...newInput }));
      paginationActions.setCurrentPage(1);
    },
  };
};

type ListedTimelogsContextType = ReturnType<typeof useInputPaginationState> & {
  queryResult?: ReturnType<typeof useGetTimelogs>;
  activeDialog:
    | { type: 'createTimelog' }
    | { type: 'editTimelog'; timelog: Timelog }
    | { type: 'deleteTimelog'; timelog: Timelog }
    | null;
  openDialog: (dialog: DialogState) => void;
  closeDialog: () => void;
  // When true, the table renders read-only (no row edit/delete actions, no
  // row-click-to-edit). Used by the account-wide reporting page, where the
  // project-scoped edit sidebar doesn't apply.
  readOnly: boolean;
  viewMode: TimelogsViewMode;
  setViewMode: (mode: TimelogsViewMode) => void;
  // Public ids of the teams selected in the TeamFilter.
  selectedTeamIds: string[];
  setSelectedTeamIds: (ids: string[]) => void;
  datePeriod: TimelogReportDatePeriod;
  setDatePeriod: (period: TimelogReportDatePeriod) => void;
  // Public ids of the selected teams' members, or null when no team is
  // selected. Used to scope the assignee picker to the chosen teams.
  teamMemberPublicIds: string[] | null;
  // `input` with the team selection folded into `assignees` (users ∪ team
  // members). This is what the queries actually run — `input` stays the raw
  // user-only selection so the assignee picker reflects manual choices.
  queryInput: GetTimelogsInput;
};

const ListedTimelogContext = createContext<ListedTimelogsContextType>({
  pagination: { limit: 25, offset: 0 },
  input: { linkedEntities: [] },
  setCurrentPage: () => {},
  setSort: () => {},
  setFilter: () => {},
  reset: () => {},
  activeDialog: null,
  openDialog: () => {},
  closeDialog: () => {},
  readOnly: false,
  viewMode: 'list',
  setViewMode: () => {},
  selectedTeamIds: [],
  setSelectedTeamIds: () => {},
  datePeriod: 'thisWeek',
  setDatePeriod: () => {},
  teamMemberPublicIds: null,
  queryInput: { linkedEntities: [] },
});

export const useListedTimelogsContext = () => {
  return useContext(ListedTimelogContext);
};

const ListedTimelogsContextProvider: FC<{
  // Optional: when provided, the list is scoped to this entity (e.g. a
  // project's Labour tab). When omitted, the list is account-wide (the
  // standalone /timelogs reporting page).
  linkedEntity?: NonNullable<GetTimelogsInput['linkedEntities']>[number];
  readOnly?: boolean;
  pageSize?: number;
  children: (state: ListedTimelogsContextType) => ReactNode;
}> = (props) => {
  const { linkedEntity, children, pageSize, readOnly = false } = props;

  const now = new Date();
  const inputPaginationState = useInputPaginationState(pageSize || 25, {
    ...(linkedEntity ? { linkedEntities: [linkedEntity] } : {}),
    ...(!linkedEntity
      ? {
          periodStart: startOfWeek(now, { weekStartsOn: 1 }).toISOString(),
          periodEnd: endOfWeek(now, { weekStartsOn: 1 }).toISOString(),
        }
      : {}),
    sortBy: linkedEntity ? 'CREATED_AT' : 'START_TIME',
    sortDirection: 'DESC',
  });

  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [datePeriod, setDatePeriod] =
    useState<TimelogReportDatePeriod>('thisWeek');

  // Timelogs are only ever assigned to individual users, so a team filter means
  // "everyone on this team": we expand the selection to member user ids.
  const { data: accountUsers = [] } = useGetMyAccountUsersExcludedDeactivated();

  const teamMemberPublicIds = useMemo(() => {
    if (!selectedTeamIds.length) return null;
    return accountUsers
      .filter((user) =>
        user.teams.items.some((team) => selectedTeamIds.includes(team.id))
      )
      .map((user) => user.publicId);
  }, [selectedTeamIds, accountUsers]);

  // Fold the team selection into `assignees` (users ∪ team members) for the
  // actual queries. `input` itself stays the raw user-only selection.
  const queryInput = useMemo<GetTimelogsInput>(() => {
    const { input } = inputPaginationState;
    if (!teamMemberPublicIds) return input;

    const explicitUserIds =
      input.assignees?.filter((a) => a.type === 'USER').map((a) => a.id) ?? [];
    const userIds = [...new Set([...explicitUserIds, ...teamMemberPublicIds])];

    // Pass a present-but-empty list when a team matches no active users, so the
    // query hooks skip the request instead of the API's "empty means everyone".
    return {
      ...input,
      assignees: userIds.map((id) => ({ id, type: 'USER' as const })),
    };
  }, [inputPaginationState, teamMemberPublicIds]);

  const queryResult = useGetTimelogs(
    queryInput,
    inputPaginationState.pagination
  );

  // View is URL-driven (`?groupBy=user`) so it's linkable and survives
  // back/forward. Only the account-wide reporting page renders the toggle;
  // other consumers just never set it.
  const history = useHistory();
  const location = useLocation();
  const viewMode: TimelogsViewMode =
    new URLSearchParams(location.search).get('groupBy') === 'user'
      ? 'byUser'
      : 'list';
  const setViewMode = (mode: TimelogsViewMode) => {
    const next = new URLSearchParams(location.search);
    if (mode === 'byUser') {
      next.set('groupBy', 'user');
    } else {
      next.delete('groupBy');
    }
    history.push({ pathname: location.pathname, search: next.toString() });
  };

  const [activeDialog, dispatch] = useReducer(dialogReducer, null);

  const openDialog = (dialog: DialogState) => {
    dispatch({ type: 'OPEN_DIALOG', payload: dialog });
  };

  const closeDialog = () => {
    dispatch({ type: 'CLOSE_DIALOG' });
  };

  let activeDialogWithTimelogData: ListedTimelogsContextType['activeDialog'] =
    null;
  switch (activeDialog?.type) {
    case 'editTimelog':
    case 'deleteTimelog': {
      const timelog = queryResult?.data?.timelogs.items.find(
        (t) => t.id === activeDialog.timelogId
      );

      if (timelog) {
        activeDialogWithTimelogData = {
          type: activeDialog.type,
          timelog,
        };
      }
      break;
    }
    default:
      activeDialogWithTimelogData = activeDialog;
  }

  const providerValue = {
    ...inputPaginationState,
    reset: () => {
      inputPaginationState.reset();
      setSelectedTeamIds([]);
      setDatePeriod('thisWeek');
    },
    queryResult: queryResult,
    activeDialog: activeDialogWithTimelogData,
    openDialog,
    closeDialog,
    readOnly,
    viewMode,
    setViewMode,
    selectedTeamIds,
    setSelectedTeamIds,
    datePeriod,
    setDatePeriod,
    teamMemberPublicIds,
    queryInput,
  };

  return (
    <ListedTimelogContext.Provider value={providerValue}>
      {children(providerValue)}
    </ListedTimelogContext.Provider>
  );
};

export {
  AssigneeFilter,
  DateFilter,
  ListedTimelogs,
  ListedTimelogsContextProvider,
  TeamFilter,
  TimelogsByUserTable,
  TimelogsTotalsSummary,
  TypeFilter,
};
